import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { useGetEntidadesQuery } from 'services/api/windx';
import { EntidadeSimples, EntidadesListParams, WindxTela } from 'helper/windx';
import {
  selectWindxIdsEntidades,
  selectWindxTelaAtiva,
  selectWindxDataSimulacao,
  setIdsEntidades,
} from 'redux/reducers/windxControlPanelReducer';

import './index.scss';

interface SeletorSubsistemasProps {
  show: boolean;
  onHide: () => void;
  // Em Comparativo SM (`permiteSelecaoVazia=true`), o usuario pode marcar
  // varias entidades. Em SH e SM Semanal, apenas uma — radio.
  permiteSelecaoVazia?: boolean;
}

/**
 * Modal pequeno e dedicado para selecao de subsistemas (subdivisao=1).
 *
 * - Quando `permiteSelecaoVazia` (Comparativo SM): modo CHECKBOX, com
 *   `idsEntidades=[]` significando "todas". SIN (id=0) e os 4 individuais
 *   sao COMBINAVEIS — cada coluna na tabela e independente, nao ha
 *   agregacao que cause dupla contagem (decisao PO 2026-05-19).
 * - Quando `!permiteSelecaoVazia` (SH e SM Semanal): modo RADIO, exige
 *   exatamente uma entidade selecionada — mutual exclusion automatica.
 *
 * ADR-024 cita a regra de "evitar dupla contagem" para somas/agregacoes,
 * caso do grafico de barras Semanal/SH. No Comparativo, cada celula
 * representa um valor pontual de uma entidade, sem somas — por isso a
 * regra nao se aplica e o usuario pode selecionar SIN + SE + S + NE + N
 * lado a lado.
 *
 * Lista apenas as 5 entidades retornadas pela rota (SIN + SE + S + NE + N),
 * sem virtualizacao nem busca — universo pequeno o suficiente.
 *
 * Substitui o caminho `subdivisao === 1` do `SeletorEntidadesLegado` no
 * `SeletorEntidades/index.tsx` (FE-LAYOUT-011 parte 2).
 */
export default function SeletorSubsistemas({
  show,
  onHide,
  permiteSelecaoVazia = false,
}: SeletorSubsistemasProps) {
  const dispatch = useDispatch();
  const idsSelecionados = useSelector(selectWindxIdsEntidades);
  const telaAtiva = useSelector(selectWindxTelaAtiva);
  const dataSimulacao = useSelector(selectWindxDataSimulacao);
  const [idsLocal, setIdsLocal] = useState<number[]>(idsSelecionados);

  const modo: 'radio' | 'checkbox' = permiteSelecaoVazia ? 'checkbox' : 'radio';

  const queryArg = useMemo<EntidadesListParams>(() => {
    const arg: EntidadesListParams = { subdivisao: 1 };
    if (telaAtiva === 'sh' || telaAtiva === 'sm') {
      arg.tela = telaAtiva as WindxTela;
    }
    // WINDX-034: dataSimulacao eh a unica fonte da verdade. Quando presente,
    // ativa modo Simulacao Antiga; quando ausente, modo Estudo Automatico.
    if (dataSimulacao) {
      arg.data_simulacao = dataSimulacao;
    }
    return arg;
  }, [telaAtiva, dataSimulacao]);

  const {
    data, isLoading, isFetching, error,
  } = useGetEntidadesQuery(queryArg, { skip: !show });

  const subsistemas = useMemo<EntidadeSimples[]>(
    () => ((data ?? []) as EntidadeSimples[]),
    [data],
  );

  const idsIndisponiveis = useMemo<Set<number>>(() => {
    if (!data) return new Set();
    return new Set(
      subsistemas.filter((e) => e.disponivel === false).map((e) => e.id),
    );
  }, [data, subsistemas]);

  // Auto-remove da selecao entidades que viraram indisponiveis.
  useEffect(() => {
    if (idsIndisponiveis.size === 0) return;
    setIdsLocal((prev) => prev.filter((id) => !idsIndisponiveis.has(id)));
    const sobra = idsSelecionados.filter((id) => !idsIndisponiveis.has(id));
    if (sobra.length !== idsSelecionados.length) {
      dispatch(setIdsEntidades(sobra));
    }
  }, [idsIndisponiveis, idsSelecionados, dispatch]);

  // Sincroniza buffer local com Redux quando o modal abre.
  useEffect(() => {
    if (show) {
      setIdsLocal(idsSelecionados);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const toggle = (id: number) => {
    if (modo === 'radio') {
      setIdsLocal([id]);
      return;
    }
    // Modo checkbox (Comparativo SM): toggle simples sem mutual exclusion.
    // SIN e os 4 individuais sao combinaveis — bug PO 2026-05-19. Ver
    // jsdoc do componente para justificativa (sem agregacao na tabela).
    setIdsLocal((prev) => (
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    ));
  };

  const limpar = () => setIdsLocal([]);

  // Bug PO 2026-05-19 9a: botao "Selecionar todos" no modo checkbox
  // (Comparativo). Inclui SIN + os 4 individuais disponiveis — mesma
  // ordem que o `data` retornado pelo backend.
  const selecionarTodos = () => {
    setIdsLocal(
      subsistemas
        .filter((e) => e.disponivel !== false)
        .map((e) => e.id),
    );
  };

  const handleOk = () => {
    // 2026-05-29: apos aplicar a selecao, NAO dispara realizarConsulta
    // automaticamente. setIdsEntidades ja marca consultaPendente=true; o
    // usuario decide quando recarregar pelo botao "Realizar Consulta" do
    // painel — janela necessaria para que ele leia o aviso de complexidade
    // (MedidorComplexidade) antes de enviar a query. Mesmo padrao adotado
    // no ModalCadastrar de simulacoes antigas (decisao PO 2026-05-26).
    dispatch(setIdsEntidades(idsLocal));
    onHide();
  };

  const totalDisponiveis = subsistemas.filter((e) => e.disponivel !== false).length;
  const semSelecaoEhTodas = idsLocal.length === 0
    && permiteSelecaoVazia
    && telaAtiva === 'sm';
  const titulo = `Selecionar subsistema${modo === 'checkbox' ? 's' : ''}${
    idsLocal.length > 0 ? ` (${idsLocal.length} selecionad${idsLocal.length === 1 ? 'o' : 'os'})` : ''
  }`;

  const okDesabilitado = !permiteSelecaoVazia && idsLocal.length === 0;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="windx-seletor-subsistemas-modal"
      data-testid="windx-seletor-subsistemas-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="h6 mb-0">{titulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="p-3 text-center">
            <Spinner animation="border" role="status" size="sm" />
            <span className="ms-2">Carregando subsistemas...</span>
          </div>
        )}

        {(error || (!isLoading && !data)) && (
          <Alert variant="danger" className="m-0">
            Não foi possível carregar os subsistemas. Tente novamente em instantes.
          </Alert>
        )}

        {data && (
          <div className="windx-seletor-subsistemas">
            {semSelecaoEhTodas && totalDisponiveis > 0 && (
              <div
                className="windx-seletor-subsistemas__hint"
                data-testid="windx-seletor-subsistemas-hint"
              >
                <strong>Nenhuma marcada inclui todas</strong>
                {' — a consulta vai retornar dados das '}
                {totalDisponiveis}
                {' entidade'}
                {totalDisponiveis === 1 ? '' : 's'}
                {' disponíve'}
                {totalDisponiveis === 1 ? 'l' : 'is'}
                .
              </div>
            )}

            <div className="windx-seletor-subsistemas__lista">
              {subsistemas.map((e) => {
                const indisponivel = idsIndisponiveis.has(e.id);
                const inputId = `windx-subsistema-${e.id}`;
                return (
                  <label
                    key={e.id}
                    htmlFor={inputId}
                    className={[
                      'windx-seletor-subsistemas__item',
                      idsLocal.includes(e.id) ? 'is-selecionado' : '',
                      indisponivel ? 'is-indisponivel' : '',
                    ].filter(Boolean).join(' ')}
                    title={indisponivel ? 'Sem dados disponíveis para a tela / data selecionada' : undefined}
                  >
                    <input
                      type={modo === 'radio' ? 'radio' : 'checkbox'}
                      id={inputId}
                      name={modo === 'radio' ? 'windx-subsistema' : undefined}
                      className={modo === 'radio' ? 'form-check-input' : 'form-check-input'}
                      checked={idsLocal.includes(e.id)}
                      onChange={() => toggle(e.id)}
                      disabled={indisponivel}
                    />
                    <span className="windx-seletor-subsistemas__rotulo">
                      <span className="windx-seletor-subsistemas__nome">{e.nome}</span>
                      {e.label && e.label !== e.nome && (
                        <span className="windx-seletor-subsistemas__label">
                          {' — '}
                          {e.label}
                        </span>
                      )}
                      {indisponivel && (
                        <span className="windx-seletor-subsistemas__badge text-muted small">
                          {' '}
                          (sem dados)
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>

            {isFetching && (
              <div className="windx-seletor-subsistemas__atualizando small text-muted">
                Atualizando…
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {modo === 'checkbox' && (
          <Button
            variant="outline-secondary"
            onClick={selecionarTodos}
            size="sm"
            disabled={totalDisponiveis === 0 || idsLocal.length === totalDisponiveis}
          >
            Selecionar todos
          </Button>
        )}
        {modo === 'checkbox' && (
          <Button variant="outline-secondary" onClick={limpar} size="sm">
            Limpar
          </Button>
        )}
        <Button variant="outline-secondary" onClick={onHide} size="sm">
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleOk}
          size="sm"
          disabled={okDesabilitado}
        >
          OK
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
